/**
 * 头像上传区（T03 / 架构 §2）
 *  - 选图 -> compressAvatar 压缩 -> 预览 -> 清除；通过 onChange(dataUrl) 回调给父组件。
 *  - 留空则父组件不传 avatar，后端存 NULL，前端 UserAvatar 用首字母兜底。
 */
import { useRef, useState } from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import DeleteIcon from '@mui/icons-material/Delete';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import { compressAvatar } from '../../utils/avatar';
import UserAvatar from '../common/UserAvatar';
import type { PublicUser } from '../../types/models';

interface AvatarUploaderProps {
  value: string | null;
  onChange: (dataUrl: string | null) => void;
}

export default function AvatarUploader({ value, onChange }: AvatarUploaderProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  async function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ''; // 允许重复选择同一文件
    if (!file) return;
    setError('');
    setBusy(true);
    try {
      const dataUrl = await compressAvatar(file);
      onChange(dataUrl);
    } catch {
      setError('图片读取失败，请换一张');
    } finally {
      setBusy(false);
    }
  }

  const previewUser: Pick<PublicUser, 'username' | 'avatar' | 'nickname'> = {
    username: 'preview',
    avatar: value,
    nickname: null,
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      <Box sx={{ position: 'relative' }}>
        <UserAvatar user={previewUser} size={64} />
        {value && (
          <IconButton
            size="small"
            onClick={() => onChange(null)}
            sx={{
              position: 'absolute',
              top: -4,
              right: -4,
              bgcolor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider',
            }}
            aria-label="清除头像"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<CameraAltIcon />}
          onClick={() => inputRef.current?.click()}
          disabled={busy}
        >
          {busy ? '处理中…' : value ? '更换头像' : '上传头像'}
        </Button>
        <input ref={inputRef} type="file" accept="image/*" hidden onChange={handleFile} />
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          选填，留空使用默认头像（≤256px，自动压缩）
        </Typography>
        {error && (
          <Typography variant="caption" color="error" sx={{ display: 'block' }}>
            {error}
          </Typography>
        )}
      </Box>
    </Box>
  );
}
